// src/pages/admin/SchoolSettings.tsx
// Centralized admin configuration page for school-specific settings

import React, { useState } from 'react';
import {
  Settings as SettingsIcon,
  Save,
  RefreshCw,
  Building2,
  Calculator,
  BookOpen,
  Layers,
  Grid3X3,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  AlertTriangle,
  Eye,
  EyeOff,
  Menu,
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useSchoolConfig } from '../../contexts/SchoolConfigContext';
import { CurriculumArea, LevelCode, SchoolConfiguration } from '../../types/schoolConfig';

// Tab definitions
interface Tab {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const tabs: Tab[] = [
  {
    id: 'identity',
    name: 'School Identity',
    icon: Building2,
    description: 'Basic school information and branding',
  },
  {
    id: 'load',
    name: 'Load Calculation',
    icon: Calculator,
    description: 'Teaching load calculation settings',
  },
  {
    id: 'curriculum',
    name: 'Learning Areas',
    icon: BookOpen,
    description: 'Learning area (faculty) definitions',
  },
  {
    id: 'levels',
    name: 'Level Codes',
    icon: Layers,
    description: 'Year level and course level definitions',
  },
  {
    id: 'grids',
    name: 'Grid Types',
    icon: Grid3X3,
    description: 'Timetable grid configurations',
  },
  {
    id: 'menu',
    name: 'Menu Visibility',
    icon: Menu,
    description: 'Show or hide navigation menu items',
  },
];

// All available menu items that can be hidden (mirrors the sidebar in Sidebar.tsx)
const allMenuItems = [
  // Staff
  { path: '/staff/directory', name: 'Staff Directory', category: 'Staff' },
  { path: '/staff/allowances', name: 'Allowances', category: 'Staff' },
  { path: '/intentions', name: 'Teaching Intentions', category: 'Staff' },
  { path: '/hr/intention-review', name: 'Review Intentions', category: 'Staff' },
  { path: '/staff/load-summaries', name: 'Load Summaries', category: 'Staff' },
  // Curriculum
  { path: '/curriculum/assignments', name: 'Teacher Assignments', category: 'Curriculum' },
  { path: '/curriculum/grids', name: 'Grid Assignments', category: 'Curriculum' },
  // Timetable
  { path: '/grid/setup', name: 'Build a Grid', category: 'Timetable' },
  { path: '/grid/multi-editor', name: 'Timetable Editor', category: 'Timetable' },
  { path: '/grid/class-management', name: 'Class List', category: 'Timetable' },
  { path: '/grid/subject-config', name: 'Subject Availability', category: 'Timetable' },
  { path: '/grid/conflicts', name: 'Conflicts', category: 'Timetable' },
  { path: '/allocation/bulk', name: 'Bulk Assign', category: 'Timetable' },
  { path: '/allocation/history', name: 'Assignment History', category: 'Timetable' },
  { path: '/grid/import', name: 'Import Grid', category: 'Timetable' },
  // Load Letters
  { path: '/load-letter/generation', name: 'Generate Letters', category: 'Load Letters' },
  { path: '/load-letter/template-settings', name: 'Letter Template', category: 'Load Letters' },
  { path: '/load-letter/staff-dashboard', name: 'My Load Letter', category: 'Load Letters' },
  // Reports
  { path: '/reports/preference-satisfaction', name: 'Preference Satisfaction', category: 'Reports' },
  { path: '/reports/preference-match', name: 'Preference Match', category: 'Reports' },
  { path: '/reports/workload-equity', name: 'Workload Equity', category: 'Reports' },
  { path: '/reports/staff-fte', name: 'Staff FTE', category: 'Reports' },
  { path: '/reports/staff-by-teaching', name: 'Staff by Teaching', category: 'Reports' },
  { path: '/reports/teaching-intentions', name: 'Teaching Intentions', category: 'Reports' },
  { path: '/reports/recruitment-gaps', name: 'Recruitment Gaps', category: 'Reports' },
  { path: '/reports/ruah-term-comparison', name: 'RUAH Term Comparison', category: 'Reports' },
  { path: '/reports/timetabled-subs', name: 'Timetabled Subs', category: 'Reports' },
  // Settings
  { path: '/admin/school-settings', name: 'School Settings', category: 'Settings' },
  { path: '/admin/intention-periods', name: 'Intention Periods', category: 'Settings' },
  { path: '/settings/allowance-templates', name: 'Allowance Templates', category: 'Settings' },
  { path: '/settings/grid-types', name: 'Grid Types', category: 'Settings' },
  { path: '/settings/user-management', name: 'Users', category: 'Settings' },
  { path: '/settings/role-management', name: 'Roles & Permissions', category: 'Settings' },
  { path: '/settings/openai-configuration', name: 'AI Configuration', category: 'Settings' },
  { path: '/staff/import-data', name: 'Import Staff Data', category: 'Settings' },
];

const SchoolSettings: React.FC = () => {
  const { addToast } = useToast();
  const confirm = useConfirm();
  const {
    config,
    updateConfig,
    curriculumAreas,
    createCurriculumArea,
    updateCurriculumArea,
    deleteCurriculumArea,
    levelCodes,
    createLevelCode,
    updateLevelCode,
    deleteLevelCode,
    gridTypes,
    hiddenMenuItems,
    setHiddenMenuItems,
    loading,
    error,
    refresh,
  } = useSchoolConfig();

  const [activeTab, setActiveTab] = useState('identity');
  const [saving, setSaving] = useState(false);
  const [editingConfig, setEditingConfig] = useState<Partial<SchoolConfiguration>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Curriculum Area editing state
  const [editingArea, setEditingArea] = useState<Partial<CurriculumArea> | null>(null);
  const [isNewArea, setIsNewArea] = useState(false);

  // Level Code editing state
  const [editingLevel, setEditingLevel] = useState<Partial<LevelCode> | null>(null);
  const [isNewLevel, setIsNewLevel] = useState(false);

  // Handle config field changes
  const handleConfigChange = (field: keyof SchoolConfiguration, value: unknown) => {
    setEditingConfig((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Get current value (edited or original)
  const getValue = <K extends keyof SchoolConfiguration>(field: K): SchoolConfiguration[K] => {
    return field in editingConfig
      ? (editingConfig[field] as SchoolConfiguration[K])
      : config[field];
  };

  // Save configuration changes
  const handleSaveConfig = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      await updateConfig(editingConfig);
      setEditingConfig({});
      setHasChanges(false);
      addToast('Configuration saved successfully', 'success');
    } catch (err) {
      addToast('Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscardChanges = () => {
    setEditingConfig({});
    setHasChanges(false);
  };

  // Curriculum Area handlers
  const handleAddArea = () => {
    setEditingArea({
      code: '',
      name: '',
      color: '#00234B',
      description: '',
      displayOrder: curriculumAreas.length + 1,
      isActive: true,
    });
    setIsNewArea(true);
  };

  const handleEditArea = (area: CurriculumArea) => {
    setEditingArea({ ...area });
    setIsNewArea(false);
  };

  const handleSaveArea = async () => {
    if (!editingArea || !editingArea.code || !editingArea.name) {
      addToast('Code and Name are required', 'error');
      return;
    }

    try {
      if (isNewArea) {
        await createCurriculumArea(editingArea as Omit<CurriculumArea, 'id' | 'createdAt' | 'updatedAt'>);
        addToast('Curriculum area created', 'success');
      } else {
        await updateCurriculumArea(editingArea.id!, editingArea);
        addToast('Curriculum area updated', 'success');
      }
      setEditingArea(null);
    } catch (err) {
      addToast('Failed to save learning area', 'error');
    }
  };

  const handleDeleteArea = async (id: string) => {
    if (!(await confirm({
      title: 'Delete learning area?',
      message: 'Are you sure you want to delete this learning area?',
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) return;

    try {
      await deleteCurriculumArea(id);
      addToast('Curriculum area deleted', 'success');
    } catch (err) {
      addToast('Failed to delete learning area', 'error');
    }
  };

  // Level Code handlers
  const handleAddLevel = () => {
    setEditingLevel({
      code: '',
      name: '',
      description: '',
      sortOrder: levelCodes.length + 1,
      isActive: true,
    });
    setIsNewLevel(true);
  };

  const handleEditLevel = (level: LevelCode) => {
    setEditingLevel({ ...level });
    setIsNewLevel(false);
  };

  const handleSaveLevel = async () => {
    if (!editingLevel || !editingLevel.code || !editingLevel.name) {
      addToast('Code and Name are required', 'error');
      return;
    }

    try {
      if (isNewLevel) {
        await createLevelCode(editingLevel as Omit<LevelCode, 'id' | 'createdAt' | 'updatedAt'>);
        addToast('Level code created', 'success');
      } else {
        await updateLevelCode(editingLevel.id!, editingLevel);
        addToast('Level code updated', 'success');
      }
      setEditingLevel(null);
    } catch (err) {
      addToast('Failed to save level code', 'error');
    }
  };

  const handleDeleteLevel = async (id: string) => {
    if (!(await confirm({
      title: 'Delete level code?',
      message: 'Are you sure you want to delete this level code?',
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) return;

    try {
      await deleteLevelCode(id);
      addToast('Level code deleted', 'success');
    } catch (err) {
      addToast('Failed to delete level code', 'error');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <PageHeader
          title="School Settings"
          subtitle="Configure school-specific settings"
          icon={<SettingsIcon className="h-8 w-8" />}
        />
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-navy-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <PageHeader
          title="School Settings"
          subtitle="Configure school-specific settings"
          icon={<SettingsIcon className="h-8 w-8" />}
        />
        <Card className="border-danger-border bg-danger-bg">
          <div className="p-6 flex items-center gap-4">
            <AlertTriangle className="h-8 w-8 text-danger-fg" />
            <div>
              <h3 className="font-medium text-danger-fg">Error Loading Configuration</h3>
              <p className="text-danger-fg">{error}</p>
              <Button onClick={refresh} className="mt-4">
                Try Again
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <PageHeader
        title="School Settings"
        subtitle="Configure school-specific settings for deployment"
        icon={<SettingsIcon className="h-8 w-8" />}
      />

      {/* Unsaved Changes Alert */}
      {hasChanges && (
        <Card className="border-warn-border bg-warn-bg">
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-warn-fg" />
              <div>
                <h3 className="font-medium text-warn-fg">Unsaved Changes</h3>
                <p className="text-sm text-warn-fg">
                  You have unsaved changes to the configuration.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleDiscardChanges} variant="outline" size="sm">
                Discard
              </Button>
              <Button onClick={handleSaveConfig} disabled={saving} size="sm">
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Tab Sidebar */}
        <div className="col-span-3">
          <Card>
            <div className="p-4 border-b">
              <h3 className="font-medium text-ink-900">Configuration</h3>
            </div>
            <nav className="p-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isSelected = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      isSelected
                        ? 'bg-navy-50 text-navy-700 border border-navy-200'
                        : 'hover:bg-ink-50 text-ink-700'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <div>
                      <div className="font-medium">{tab.name}</div>
                      <div className="text-xs text-ink-500">{tab.description}</div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </Card>
        </div>

        {/* Content Area */}
        <div className="col-span-9">
          {/* School Identity Tab */}
          {activeTab === 'identity' && (
            <Card>
              <div className="p-6 border-b">
                <div className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-navy-600" />
                  <div>
                    <h2 className="text-section text-ink-900">School Identity</h2>
                    <p className="text-ink-500">Basic school information used throughout the application</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      School Name *
                    </label>
                    <input
                      type="text"
                      value={getValue('schoolName')}
                      onChange={(e) => handleConfigChange('schoolName', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Short Name
                    </label>
                    <input
                      type="text"
                      value={getValue('schoolShortName')}
                      onChange={(e) => handleConfigChange('schoolShortName', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                      placeholder="e.g., SCS"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Principal Name
                    </label>
                    <input
                      type="text"
                      value={getValue('principalName')}
                      onChange={(e) => handleConfigChange('principalName', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Timezone
                    </label>
                    <select
                      value={getValue('timezone')}
                      onChange={(e) => handleConfigChange('timezone', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    >
                      <option value="Australia/Melbourne">Australia/Melbourne</option>
                      <option value="Australia/Sydney">Australia/Sydney</option>
                      <option value="Australia/Brisbane">Australia/Brisbane</option>
                      <option value="Australia/Adelaide">Australia/Adelaide</option>
                      <option value="Australia/Perth">Australia/Perth</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={getValue('schoolAddress')}
                      onChange={(e) => handleConfigChange('schoolAddress', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      value={getValue('schoolPhone')}
                      onChange={(e) => handleConfigChange('schoolPhone', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={getValue('schoolEmail')}
                      onChange={(e) => handleConfigChange('schoolEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Website
                    </label>
                    <input
                      type="text"
                      value={getValue('schoolWebsite')}
                      onChange={(e) => handleConfigChange('schoolWebsite', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Banner Image URL
                    </label>
                    <input
                      type="text"
                      value={getValue('bannerUrl')}
                      onChange={(e) => handleConfigChange('bannerUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                      placeholder="/pageheader.jpg"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Tagline
                    </label>
                    <input
                      type="text"
                      value={getValue('schoolTagline')}
                      onChange={(e) => handleConfigChange('schoolTagline', e.target.value)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Load Calculation Tab */}
          {activeTab === 'load' && (
            <Card>
              <div className="p-6 border-b">
                <div className="flex items-center gap-3">
                  <Calculator className="h-6 w-6 text-navy-600" />
                  <div>
                    <h2 className="text-section text-ink-900">Load Calculation Settings</h2>
                    <p className="text-ink-500">Configure values used for teaching load calculations</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Base Minutes per Fortnight
                    </label>
                    <input
                      type="number"
                      value={getValue('baseMinutesPerFortnight')}
                      onChange={(e) => handleConfigChange('baseMinutesPerFortnight', parseInt(e.target.value) || 2220)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                    <p className="text-xs text-ink-500 mt-1">
                      Total teaching minutes available per fortnight for a 1.0 FTE staff member
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Default Minutes per Line
                    </label>
                    <input
                      type="number"
                      value={getValue('defaultMinutesPerLine')}
                      onChange={(e) => handleConfigChange('defaultMinutesPerLine', parseInt(e.target.value) || 440)}
                      className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                    />
                    <p className="text-xs text-ink-500 mt-1">
                      Default teaching minutes per timetable line
                    </p>
                  </div>
                </div>

                <div className="bg-navy-50 border border-navy-200 rounded-lg p-4">
                  <h4 className="font-medium text-navy-800 mb-2">Load Calculation Formula</h4>
                  <div className="text-sm text-navy-700 space-y-1">
                    <p>Target Minutes = Base Minutes per Fortnight x FTE</p>
                    <p>Teaching Minutes = Sum of (Class Minutes per line)</p>
                    <p>Utilization = (Teaching Minutes + Allowance Minutes) / Target Minutes x 100%</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    Year Levels
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[7, 8, 9, 10, 11, 12].map((year) => (
                      <label
                        key={year}
                        className="flex items-center gap-2 px-3 py-2 border border-ink-300 rounded-lg cursor-pointer hover:bg-ink-50"
                      >
                        <input
                          type="checkbox"
                          checked={getValue('yearLevels').includes(year)}
                          onChange={(e) => {
                            const currentLevels = getValue('yearLevels');
                            const newLevels = e.target.checked
                              ? [...currentLevels, year].sort((a, b) => a - b)
                              : currentLevels.filter((y) => y !== year);
                            handleConfigChange('yearLevels', newLevels);
                          }}
                          className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded"
                        />
                        <span className="text-sm">Year {year}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Curriculum Areas Tab */}
          {activeTab === 'curriculum' && (
            <Card>
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-6 w-6 text-navy-600" />
                    <div>
                      <h2 className="text-section text-ink-900">Learning Areas</h2>
                      <p className="text-ink-500">Learning areas used across timetabling, staffing and reports</p>
                    </div>
                  </div>
                  <Button onClick={handleAddArea} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Area
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-ink-200">
                  <thead className="bg-ink-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Color
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-ink-200">
                    {curriculumAreas.map((area) => (
                      <tr key={area.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono tabular-nums font-medium text-ink-900">
                          {area.code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">
                          {area.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                          <div className="flex items-center gap-2">
                            <div
                              className="h-4 w-4 rounded"
                              style={{ backgroundColor: area.color }}
                            />
                            <span>{area.color}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono tabular-nums text-ink-500">
                          {area.displayOrder}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              area.isActive
                                ? 'bg-ok-bg text-ok-fg'
                                : 'bg-ink-100 text-ink-900'
                            }`}
                          >
                            {area.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => handleEditArea(area)}
                            className="text-navy-600 hover:text-navy-900 mr-3"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteArea(area.id)}
                            className="text-danger-fg hover:text-danger-fg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Level Codes Tab */}
          {activeTab === 'levels' && (
            <Card>
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Layers className="h-6 w-6 text-navy-600" />
                    <div>
                      <h2 className="text-section text-ink-900">Level Codes</h2>
                      <p className="text-ink-500">Year level and course level definitions</p>
                    </div>
                  </div>
                  <Button onClick={handleAddLevel} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Level
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-ink-200">
                  <thead className="bg-ink-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Sort Order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-ink-200">
                    {levelCodes.map((level) => (
                      <tr key={level.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono tabular-nums font-medium text-ink-900">
                          {level.code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">
                          {level.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-ink-500 max-w-xs truncate">
                          {level.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono tabular-nums text-ink-500">
                          {level.sortOrder}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              level.isActive
                                ? 'bg-ok-bg text-ok-fg'
                                : 'bg-ink-100 text-ink-900'
                            }`}
                          >
                            {level.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => handleEditLevel(level)}
                            className="text-navy-600 hover:text-navy-900 mr-3"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteLevel(level.id)}
                            className="text-danger-fg hover:text-danger-fg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Grid Types Tab */}
          {activeTab === 'grids' && (
            <Card>
              <div className="p-6 border-b">
                <div className="flex items-center gap-3">
                  <Grid3X3 className="h-6 w-6 text-navy-600" />
                  <div>
                    <h2 className="text-section text-ink-900">Grid Types</h2>
                    <p className="text-ink-500">Timetable grid configurations</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {gridTypes.map((grid) => (
                    <div
                      key={grid.id}
                      className="border border-ink-200 rounded-lg p-4 hover:border-navy-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-ink-900">{grid.name}</h3>
                          <p className="text-sm text-ink-500">{grid.description}</p>
                        </div>
                        {grid.isSystemDefault && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-navy-100 text-navy-800">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-ink-500">Class Minutes:</span>
                          <span className="font-mono tabular-nums font-medium">{grid.defaultClassMinutes} min</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-500">Periods/Day:</span>
                          <span className="font-mono tabular-nums font-medium">{grid.periodsPerDay}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-500">Days/Cycle:</span>
                          <span className="font-mono tabular-nums font-medium">{grid.daysPerCycle}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-500">Full Year:</span>
                          <span className="font-medium">{grid.fullYear ? 'Yes' : 'No'}</span>
                        </div>
                        {grid.lineStructure && grid.lineStructure.length > 0 && (
                          <div>
                            <span className="text-ink-500">Lines:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {grid.lineStructure.map((line) => (
                                <span
                                  key={line}
                                  className="font-mono tabular-nums px-2 py-0.5 bg-ink-100 text-ink-700 rounded text-xs"
                                >
                                  {line}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Menu Visibility Tab */}
          {activeTab === 'menu' && (
            <Card>
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Menu className="h-6 w-6 text-navy-600" />
                    <div>
                      <h2 className="text-section text-ink-900">Menu Visibility</h2>
                      <p className="text-ink-500">Control which menu items are visible to users</p>
                    </div>
                  </div>
                  {hiddenMenuItems.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (await confirm({
                          title: 'Show all menu items?',
                          message: 'Are you sure you want to show all hidden menu items?',
                          confirmLabel: 'Show all',
                          variant: 'primary',
                        })) {
                          setHiddenMenuItems([]);
                          addToast('All menu items are now visible', 'success');
                        }
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Show All ({hiddenMenuItems.length} hidden)
                    </Button>
                  )}
                </div>
              </div>
              <div className="p-6">
                {/* Instructions */}
                <div className="bg-navy-50 border border-navy-200 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-navy-800 mb-2">How it works</h4>
                  <ul className="text-sm text-navy-700 space-y-1 list-disc list-inside">
                    <li>Hidden items will not appear in the sidebar for any user</li>
                    <li>Admins can toggle the "show hidden" button in the sidebar to reveal hidden items</li>
                    <li>Hidden items appear with a strikethrough and amber background when revealed</li>
                    <li>Use this to declutter the navigation by hiding unused or development features</li>
                  </ul>
                </div>

                {/* Menu items grouped by category */}
                {Object.entries(
                  allMenuItems.reduce((acc, item) => {
                    if (!acc[item.category]) acc[item.category] = [];
                    acc[item.category].push(item);
                    return acc;
                  }, {} as Record<string, typeof allMenuItems>)
                ).map(([category, items]) => (
                  <div key={category} className="mb-6">
                    <h3 className="text-sm font-semibold text-ink-700 uppercase tracking-wider mb-3">
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {items.map((item) => {
                        const isHidden = hiddenMenuItems.includes(item.path);
                        return (
                          <div
                            key={item.path}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                              isHidden
                                ? 'bg-warn-bg border-warn-border'
                                : 'bg-white border-ink-200 hover:border-ink-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {isHidden ? (
                                <EyeOff className="h-4 w-4 text-warn-fg" />
                              ) : (
                                <Eye className="h-4 w-4 text-ink-400" />
                              )}
                              <div>
                                <span className={`text-sm font-medium ${isHidden ? 'text-warn-fg line-through' : 'text-ink-900'}`}>
                                  {item.name}
                                </span>
                                <span className="text-xs text-ink-400 ml-2 font-mono">
                                  {item.path}
                                </span>
                                {item.hint && (
                                  <p className="text-xs text-warn-fg mt-0.5">{item.hint}</p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                const newHidden = isHidden
                                  ? hiddenMenuItems.filter((p) => p !== item.path)
                                  : [...hiddenMenuItems, item.path];
                                await setHiddenMenuItems(newHidden);
                                addToast(
                                  isHidden
                                    ? `"${item.name}" is now visible`
                                    : `"${item.name}" is now hidden`,
                                  'success'
                                );
                              }}
                              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                isHidden
                                  ? 'bg-warn-bg text-warn-fg hover:bg-warn-border'
                                  : 'bg-ink-100 text-ink-500 hover:bg-ink-200'
                              }`}
                            >
                              {isHidden ? 'Show' : 'Hide'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Recommended defaults */}
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-semibold text-ink-700 mb-3">Quick Actions</h3>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        await setHiddenMenuItems([]);
                        addToast('All menu items are now visible', 'success');
                      }}
                    >
                      Reset (Show All)
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Curriculum Area Modal */}
      {editingArea && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-card-title text-ink-900 mb-4">
              {isNewArea ? 'Add Learning Area' : 'Edit Learning Area'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={editingArea.code || ''}
                  onChange={(e) => setEditingArea({ ...editingArea, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g., ENG, MAT"
                  maxLength={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={editingArea.name || ''}
                  onChange={(e) => setEditingArea({ ...editingArea, name: e.target.value })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g., English, Mathematics"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editingArea.color || '#00234B'}
                    onChange={(e) => setEditingArea({ ...editingArea, color: e.target.value })}
                    className="h-10 w-10 border border-ink-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={editingArea.color || '#00234B'}
                    onChange={(e) => setEditingArea({ ...editingArea, color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={editingArea.displayOrder || 0}
                  onChange={(e) => setEditingArea({ ...editingArea, displayOrder: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                  min="0"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="areaActive"
                  checked={editingArea.isActive ?? true}
                  onChange={(e) => setEditingArea({ ...editingArea, isActive: e.target.checked })}
                  className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded"
                />
                <label htmlFor="areaActive" className="text-sm text-ink-700">Active</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditingArea(null)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSaveArea}>
                <Check className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Level Code Modal */}
      {editingLevel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-card-title text-ink-900 mb-4">
              {isNewLevel ? 'Add Level Code' : 'Edit Level Code'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Code *</label>
                <input
                  type="text"
                  value={editingLevel.code || ''}
                  onChange={(e) => setEditingLevel({ ...editingLevel, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g., DI, EN, 12"
                  maxLength={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={editingLevel.name || ''}
                  onChange={(e) => setEditingLevel({ ...editingLevel, name: e.target.value })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g., Discover, Units 1 & 2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editingLevel.description || ''}
                  onChange={(e) => setEditingLevel({ ...editingLevel, description: e.target.value })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                  placeholder="e.g., Foundation level subjects"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Sort Order</label>
                <input
                  type="number"
                  value={editingLevel.sortOrder || 0}
                  onChange={(e) => setEditingLevel({ ...editingLevel, sortOrder: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                  min="0"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="levelActive"
                  checked={editingLevel.isActive ?? true}
                  onChange={(e) => setEditingLevel({ ...editingLevel, isActive: e.target.checked })}
                  className="h-4 w-4 text-navy-600 focus:ring-navy-500 border-ink-300 rounded"
                />
                <label htmlFor="levelActive" className="text-sm text-ink-700">Active</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditingLevel(null)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSaveLevel}>
                <Check className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolSettings;
